import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/feature/LanguageSwitcher';

export default function ConsultingPage() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = '組織開発コンサルティング | 株式会社LIKES - 福岡市 人材育成 経営改善 マンダラチャート';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '株式会社LIKESの組織開発コンサルティング。平均1億円強の収支改善実績。マンダラチャート認定講師による人材育成、人事評価制度構築、組織風土改革で福岡市の企業の持続的成長を支援します。');
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/consulting`);
    }
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemas = [
      { id: 'schema-service-c', data: { '@context': 'https://schema.org', '@type': 'Service', name: '組織開発及び人材育成コンサルティング', provider: { '@type': 'Organization', '@id': `${siteUrl}/#organization` } } },
      { id: 'schema-breadcrumb-c', data: { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'ホーム', item: `${siteUrl}/` }, { '@type': 'ListItem', position: 2, name: '組織開発コンサルティング', item: `${siteUrl}/services/consulting` }] } },
    ];
    schemas.forEach(({ id, data }) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(data);
      script.id = id;
      document.head.appendChild(script);
    });
    return () => { schemas.forEach(({ id }) => { document.getElementById(id)?.remove(); }); };
  }, []);

  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const details = [
    {
      imgSrc: 'https://public.readdy.ai/ai/img_res/c7a9a1390ce00c53f2980dfa52482c0e.jpg',
      imgAlt: '経営改善実績',
      badge: t('page.consulting.detail1.badge'),
      title: t('page.consulting.detail1.title'),
      p1: t('page.consulting.detail1.p1'),
      p2: t('page.consulting.detail1.p2'),
      reverse: false,
    },
    {
      imgSrc: 'https://readdy.ai/api/search-image?query=Professional%20training%20workshop%20with%20diverse%20employees%20learning%20leadership%20skills%20and%20team%20development%20in%20bright%20modern%20training%20room%2C%20instructor%20teaching%20business%20professionals%2C%20collaborative%20learning%20environment%20with%20engaged%20participants&width=800&height=600&seq=consulting-detail-002&orientation=landscape',
      imgAlt: '人材育成の専門性',
      badge: t('page.consulting.detail2.badge'),
      title: t('page.consulting.detail2.title'),
      p1: t('page.consulting.detail2.p1'),
      p2: t('page.consulting.detail2.p2'),
      reverse: true,
    },
    {
      imgSrc: 'https://readdy.ai/api/search-image?query=Mandala%20chart%20strategic%20planning%20tool%20displayed%20on%20large%20screen%20in%20modern%20office%20with%20business%20team%20analyzing%20goals%20and%20objectives%2C%20structured%20thinking%20framework%2C%20visual%20management%20system%20with%20clean%20professional%20atmosphere&width=800&height=600&seq=consulting-detail-003&orientation=landscape',
      imgAlt: 'マンダラチャート活用',
      badge: t('page.consulting.detail3.badge'),
      title: t('page.consulting.detail3.title'),
      p1: t('page.consulting.detail3.p1'),
      p2: t('page.consulting.detail3.p2'),
      reverse: false,
    },
  ];

  const stats = [
    { value: t('page.consulting.stats1.value'), label: t('page.consulting.stats1.label'), desc: t('page.consulting.stats1.desc') },
    { value: t('page.consulting.stats2.value'), label: t('page.consulting.stats2.label'), desc: t('page.consulting.stats2.desc') },
    { value: t('page.consulting.stats3.value'), label: t('page.consulting.stats3.label'), desc: t('page.consulting.stats3.desc') },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex flex-col leading-none cursor-pointer">
            <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.18em' }} className="text-[#255485] font-bold text-2xl tracking-widest uppercase">LIKES</span>
            <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.22em' }} className="text-[#255485]/70 text-[9px] font-medium tracking-[0.22em] uppercase mt-0.5">Co, Ltd</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/#vision" className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium cursor-pointer">{t('nav.vision')}</Link>
            <Link to="/#services" className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium cursor-pointer">{t('nav.services')}</Link>
            <Link to="/case-study" className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium cursor-pointer">{t('nav.caseStudy')}</Link>
            <Link to="/#csr" className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium cursor-pointer">{t('nav.csr')}</Link>
            <Link to="/#company" className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium cursor-pointer">{t('nav.company')}</Link>
            <LanguageSwitcher />
            <button onClick={scrollToContact} className="bg-[#255485] text-white px-6 py-2.5 rounded-lg hover:bg-[#1d4166] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer">{t('nav.contact')}</button>
          </nav>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <nav className="flex flex-col px-6 py-4 space-y-3">
              <div className="flex items-center gap-2 py-1"><span className="text-xs text-gray-500">Language</span><LanguageSwitcher /></div>
              <Link to="/#vision" className="text-gray-700 hover:text-[#255485] text-sm font-medium cursor-pointer py-2">{t('nav.vision')}</Link>
              <Link to="/#services" className="text-gray-700 hover:text-[#255485] text-sm font-medium cursor-pointer py-2">{t('nav.services')}</Link>
              <Link to="/case-study" className="text-gray-700 hover:text-[#255485] text-sm font-medium cursor-pointer py-2">{t('nav.caseStudy')}</Link>
              <Link to="/#csr" className="text-gray-700 hover:text-[#255485] text-sm font-medium cursor-pointer py-2">{t('nav.csr')}</Link>
              <Link to="/#company" className="text-gray-700 hover:text-[#255485] text-sm font-medium cursor-pointer py-2">{t('nav.company')}</Link>
              <button onClick={scrollToContact} className="bg-[#255485] text-white px-6 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer text-center">{t('nav.contact')}</button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://readdy.ai/api/search-image?query=Professional%20business%20consulting%20meeting%20with%20executives%20and%20consultants%20discussing%20organizational%20development%20strategy%20in%20modern%20bright%20office%2C%20collaborative%20atmosphere%2C%20leadership%20training%20concept%2C%20high%20quality%20corporate%20photography%20with%20natural%20lighting%20and%20professional%20setting&width=1920&height=600&seq=consulting-hero-001&orientation=landscape" alt="組織開発コンサルティング" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <p className="text-white/90 text-xs sm:text-sm font-medium tracking-wider mb-4">{t('page.consulting.hero.eyebrow')}</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" style={{ whiteSpace: 'pre-line' }}>{t('page.consulting.hero.title')}</h1>
            <p className="text-white/90 text-base sm:text-lg leading-relaxed" style={{ whiteSpace: 'pre-line' }}>{t('page.consulting.hero.subtitle')}</p>
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 gap-16">
            {details.map((item) => (
              <div key={item.badge} className={`flex flex-col ${item.reverse ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 md:gap-12 items-start`}>
                <div className="w-full md:w-1/2">
                  <img src={item.imgSrc} alt={item.imgAlt} className="w-full h-64 md:h-80 object-cover rounded-lg" />
                </div>
                <div className="w-full md:w-1/2">
                  <div className="inline-block bg-[#255485]/10 text-[#255485] px-4 py-2 rounded-full text-sm font-medium mb-4">{item.badge}</div>
                  <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-6" style={{ whiteSpace: 'pre-line' }}>{item.title}</h3>
                  <p className="text-gray-700 leading-relaxed mb-6">{item.p1}</p>
                  <p className="text-gray-700 leading-relaxed">{item.p2}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">{t('page.consulting.stats.title')}</h2>
            <p className="text-gray-600">{t('page.consulting.stats.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="bg-white p-10 rounded-lg text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#255485] mb-3">{s.value}</div>
                <div className="text-gray-600 text-sm font-medium">{s.label}</div>
                <p className="text-gray-500 text-xs mt-3" style={{ whiteSpace: 'pre-line' }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-6" style={{ whiteSpace: 'pre-line' }}>{t('page.consulting.cta.title')}</h2>
          <p className="text-gray-600 leading-relaxed mb-10" style={{ whiteSpace: 'pre-line' }}>{t('page.consulting.cta.body')}</p>
          <button onClick={scrollToContact} className="bg-[#255485] text-white px-12 py-4 rounded-lg hover:bg-[#1d4166] transition-colors duration-200 text-base font-medium whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
            {t('page.consulting.cta.button')}<i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <Link to="/" className="flex flex-col leading-none mb-4 cursor-pointer">
                <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.18em' }} className="text-white font-bold text-2xl tracking-widest uppercase">LIKES</span>
                <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.22em' }} className="text-white/70 text-[9px] font-medium tracking-[0.22em] uppercase mt-0.5">Co, Ltd</span>
              </Link>
              <p className="text-white/70 text-sm leading-relaxed" style={{ whiteSpace: 'pre-line' }}>{t('subpage.footer.tagline')}</p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">{t('subpage.footer.nav')}</h3>
              <nav className="space-y-3">
                <Link to="/#vision" className="block text-white/70 hover:text-white transition-colors duration-200 text-sm cursor-pointer">{t('nav.vision')}</Link>
                <Link to="/#services" className="block text-white/70 hover:text-white transition-colors duration-200 text-sm cursor-pointer">{t('nav.services')}</Link>
                <Link to="/#csr" className="block text-white/70 hover:text-white transition-colors duration-200 text-sm cursor-pointer">{t('nav.csr')}</Link>
                <Link to="/#company" className="block text-white/70 hover:text-white transition-colors duration-200 text-sm cursor-pointer">{t('nav.company')}</Link>
                <button onClick={scrollToContact} className="block text-white/70 hover:text-white transition-colors duration-200 text-sm cursor-pointer text-left">{t('nav.contact')}</button>
              </nav>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">{t('subpage.footer.contact')}</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-4">{t('subpage.footer.address')}</p>
              <button onClick={scrollToContact} className="text-white/70 hover:text-white transition-colors duration-200 text-sm underline cursor-pointer">{t('nav.contact')}</button>
              <div className="mt-8">
                <h3 className="text-white font-semibold mb-3">{t('subpage.footer.related')}</h3>
                <a href="https://www.ikimonogakari.jp/" target="_blank" rel="noopener noreferrer nofollow" className="inline-block cursor-pointer">
                  <img src="https://static.readdy.ai/image/26970d9518e2e09002ea7ff933ab8760/5b8b09492cb52be1efa246b8b18519da.png" alt="いきものがかり" className="h-14 w-auto object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-white/50 text-sm">{t('subpage.footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
